/**
 * Maintenance entry row for the Maintenance blade.
 *
 * Renders one log entry. Open entries show the issue, who reported it and when,
 * and (with permission) a Resolve action that expands an inline resolution form
 * in place. Resolved entries render read-only with their resolution and who
 * closed them. Replaces the old per-entry resolve dialog.
 *
 * Reads plain-JS log objects from the new odl-app maintenanceLogs slice.
 * Patches via PATCH /api/odl-app/sites/<siteId>/maintenance-logs/<id>/.
 *
 * Contains:
 * - MaintenanceEntryRow — single entry row + inline resolve form (default export)
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Card, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { patchMaintenanceLog } from '@sophocles/api/odlApp';
import { formatDateForSubmission, userRefName } from '@sophocles/utils/formatUtils';

// Filled multiline vertically centers a short value inside a tall slot;
// anchor the textarea to the top so typed text lays out from line 1 down.
const multilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

function formatDay(dateString) {
  if (!dateString) return null;
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

function ResolveForm({ log, siteId, onDone }) {
  const { t } = useTranslation();

  const reportedDate = log.date_reported ?? null;
  const minDate = reportedDate ? parseISO(reportedDate) : undefined;

  const [dateResolved, setDateResolved] = useState(new Date());
  const [resolution, setResolution] = useState('');
  const [technician, setTechnician] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = resolution.trim().length > 0 && technician.length <= 100 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    patchMaintenanceLog(siteId, log.id, {
      date_resolved: formatDateForSubmission(dateResolved),
      resolution: resolution.trim(),
      resolved_technician: technician.trim(),
    })
      .then(patch => {
        onDone?.(patch);
      })
      .catch(() => {
        setError(t('maintenance.saveFailed', 'Failed to save. Please try again.'));
        setSubmitting(false);
      });
  };

  return (
    <Stack spacing={2} sx={{ mt: 1.5 }}>
      <DatePicker
        label={t('sites.dateOfResolution')}
        value={dateResolved}
        minDate={minDate}
        maxDate={new Date()}
        onChange={setDateResolved}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
      <TextField
        required
        fullWidth
        multiline
        minRows={2}
        size="small"
        label={t('sites.resolution')}
        value={resolution}
        onChange={e => setResolution(e.target.value)}
        error={Boolean(error)}
        helperText={error ?? undefined}
        sx={multilineSx}
      />
      <TextField
        fullWidth
        size="small"
        label={t('sites.technician')}
        value={technician}
        onChange={e => setTechnician(e.target.value)}
        error={technician.length > 100}
        inputProps={{ maxLength: 100 }}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" size="small" onClick={() => onDone?.()} disabled={submitting}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" size="small" onClick={handleSubmit} disabled={!canSubmit}>
          {t('actions.submit')}
        </Button>
      </Stack>
    </Stack>
  );
}

ResolveForm.propTypes = {
  log: PropTypes.object.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDone: PropTypes.func.isRequired,
};

/**
 * @param {object}        props
 * @param {object}        props.log              - plain JS maintenance log entry from the new contract
 * @param {number|string} props.siteId           - required for the patch endpoint
 * @param {boolean}       props.canEdit          - whether the user may resolve open entries
 * @param {Function}      props.onPatched        - called with the patch payload after a successful resolve
 * @param {boolean}       [props.defaultResolving] - when true, opens the ResolveForm immediately
 */
function MaintenanceEntryRow({ log, siteId, canEdit, onPatched, defaultResolving }) {
  const { t } = useTranslation();
  const [resolving, setResolving] = useState(Boolean(defaultResolving));

  const isResolved = log.date_resolved != null;
  const issue = log.issue ?? '';
  const reportedBy = userRefName(log.entered_by_user ?? null);
  const reportedOn = formatDay(log.date_reported ?? null);

  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>
            {issue}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {[reportedOn, reportedBy].filter(Boolean).join(' · ')}
          </Typography>
        </Box>

        {isResolved ? (
          <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ flexShrink: 0, mt: 0.25 }} />
        ) : (
          canEdit &&
          !resolving && (
            <Button size="small" onClick={() => setResolving(true)} sx={{ flexShrink: 0 }}>
              {t('actions.resolve', 'Resolve')}
            </Button>
          )
        )}
      </Stack>

      {isResolved && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid var(--md-outline-variant)' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {log.resolution ?? ''}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {t('maintenance.resolvedMeta', {
              date: formatDay(log.date_resolved ?? null),
              name: userRefName(log.resolved_by_user ?? null) || log.resolved_technician || '—',
              defaultValue: 'Resolved {{date}} · {{name}}',
            })}
          </Typography>
        </Box>
      )}

      {!isResolved && resolving && (
        <ResolveForm
          log={log}
          siteId={siteId}
          onDone={patch => {
            setResolving(false);
            if (patch) onPatched?.(patch);
          }}
        />
      )}
    </Card>
  );
}

MaintenanceEntryRow.propTypes = {
  log: PropTypes.object.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onPatched: PropTypes.func,
  defaultResolving: PropTypes.bool,
};

export default MaintenanceEntryRow;
